import { useRouteRef } from '@backstage/core-plugin-api';
import {
  entityRouteRef,
  useStarredEntities,
} from '@backstage/plugin-catalog-react';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';
import {
  getSurfaceBorder,
  getSurfaceColor,
  getThemeTokens,
  radii,
} from '../../../theme/tokens';

const kindLabel: Record<string, string> = {
  component: 'Component',
  api: 'API',
  system: 'System',
  domain: 'Domain',
  resource: 'Resource',
  group: 'Group',
  user: 'User',
  template: 'Template',
  location: 'Location',
};

const parseRef = (ref: string) => {
  const match = ref.match(/^(\w+):(?:([^/]+)\/)?(.+)$/);
  if (!match) return { kind: 'component', namespace: 'default', name: ref };
  return {
    kind: match[1],
    namespace: match[2] || 'default',
    name: match[3],
  };
};

export const StarredEntitiesWidget = () => {
  const theme = useTheme();
  const tokens = getThemeTokens(theme);
  const { starredEntities } = useStarredEntities();
  const entityRoute = useRouteRef(entityRouteRef);

  const starred = Array.from(starredEntities).map(ref => ({
    ref,
    ...parseRef(ref),
  }));

  if (!starred.length) {
    return (
      <Paper sx={{ px: 3, py: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Starred
        </Typography>
        <Box
          sx={{
            backgroundColor: getSurfaceColor(theme, 'subtle'),
            border: `1px solid ${getSurfaceBorder(theme)}`,
            borderRadius: `${radii.md}px`,
            px: 3,
            py: 3,
            textAlign: 'center',
          }}
        >
          <BookmarkBorderIcon
            sx={{ color: 'text.disabled', fontSize: 32, mb: 1 }}
          />
          <Typography color="text.secondary" variant="body2">
            Star items in the catalog to pin them here.
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper sx={{ px: 0, py: 3 }}>
      <Box
        sx={{
          px: 3,
          mb: 1.5,
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="h6">Starred</Typography>
        <Typography variant="caption" color="text.secondary">
          {starred.length} {starred.length === 1 ? 'item' : 'items'}
        </Typography>
      </Box>
      <Stack spacing={0}>
        {starred.map(({ ref, kind, namespace, name }) => (
          <Link
            key={ref}
            component={RouterLink}
            to={entityRoute({ kind, namespace, name })}
            underline="none"
            color="inherit"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              px: 2.5,
              py: 1.25,
              transition: 'background-color 150ms ease',
              '&:hover': {
                backgroundColor: getSurfaceColor(theme, 'subtle'),
              },
            }}
          >
            <StarRoundedIcon
              sx={{ color: '#f0b400', fontSize: 18, flexShrink: 0 }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle2" sx={{ lineHeight: 1.35 }} noWrap>
                {name}
              </Typography>
            </Box>
            <Typography
              variant="caption"
              sx={{
                color: tokens.textMuted,
                flexShrink: 0,
                fontSize: '0.65rem',
                fontWeight: 600,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
              }}
            >
              {kindLabel[kind] ?? kind}
            </Typography>
          </Link>
        ))}
      </Stack>
    </Paper>
  );
};
